class InventoryLowStockManager {
  constructor() {
    this.variantChangeUnsubscriber = null;
    this.init();
  }

  init() {
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      this.handleVariantChange.bind(this)
    );

    setTimeout(() => {
      this.initializeAllInventoryElements();
    }, 100);
  }

  initializeAllInventoryElements() {
    const inventoryElements = document.querySelectorAll('.product__inventory--low:not([data-is-from-metafield="true"])');
    const metafieldElements = document.querySelectorAll('.product__inventory--low[data-is-from-metafield="true"]');

    inventoryElements.forEach(element => {
      this.updateInventoryElement(element);
    });

    metafieldElements.forEach(element => {
      this.updateInventoryElement(element, null, true);
    });

  }

  handleVariantChange(event) {
    const variant = event.data?.variant;
    const sectionId = event.data?.sectionId;

    if (!variant || !sectionId) {
      console.error('No variant or sectionId data in event');
      return;
    }

    const productInfo = document.querySelector(`product-info[data-section="${sectionId}"]`);
    if (productInfo) {
      const inventoryElement = productInfo.querySelector('.product__inventory--low:not([data-is-from-metafield="true"])');
      if (inventoryElement) {
        this.updateInventoryElement(inventoryElement, variant);
      } else {
        console.error('No inventory element found in section:', sectionId);
      }
    } else {
      console.error('No product-info found for section:', sectionId);
    }
  }

  updateInventoryElement(element, variant = null, isFromMetafield = false) {
    if (!element) return;

    let inventoryQuantity = null;

    if (variant && variant.inventory_quantity !== undefined) {
      inventoryQuantity = variant.inventory_quantity;
    } else {
      const qtyLevel = element.getAttribute('data-qty-level');
      if (qtyLevel) {
        inventoryQuantity = parseInt(qtyLevel, 10);
      }
    }
    
    if (isFromMetafield && inventoryQuantity > 0) {
      element.style.display = 'block';
      return;
    }

    const shouldShow = inventoryQuantity !== null && inventoryQuantity !== undefined && inventoryQuantity <= 15 && inventoryQuantity > 0;

    if (shouldShow) {
      element.style.display = 'block';
      element.setAttribute('data-qty-level', inventoryQuantity);

      const textElement = element.querySelector('.product__inventory--low-text');
      if (textElement) {
        const localizedText = this.getLocalizedText(inventoryQuantity);
        textElement.textContent = localizedText;
      }
    } else {
      element.style.display = 'none';
    }
  }

  getLocalizedText(quantity) {
    if (window.inventoryTranslations) {
      return window.inventoryTranslations.replace('[quantity]', quantity);
    }

    return `Only ${quantity} products left!`;
  }

  destroy() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.inventoryManager = new InventoryLowStockManager();
});

if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const inventoryElements = node.querySelectorAll ? node.querySelectorAll('.product__inventory--low:not([data-is-from-metafield="true"])') : [];
            if (inventoryElements.length > 0 || node.classList?.contains('product__inventory--low:not([data-is-from-metafield="true"])')) {
              setTimeout(() => {
                const manager = window.inventoryManager;
                if (manager) {
                  manager.initializeAllInventoryElements();
                }
              }, 100);
            }
          }
        });
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

class ProductStickyBar extends HTMLElement {
  constructor() {
    super();
    this.buyButtons = null;
    this.mainProductForm = null;
    this.submitButton = null;
    this.priceElements = null;
    this.isVisible = false;
    this.headerHeight = 0;
    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.scrollHandler = null;
    this.resizeHandler = null;
    this.variantChangeUnsubscriber = null;
    this.edgeThreshold = 10;
    this.init();
  }

  connectedCallback() {
    this.classList.remove('show');
    this.isVisible = false;
    this.style.transition = 'transform 0.25s ease';

    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      this.style.transform = 'translateY(100%)';
      this.style.bottom = '0';
      this.style.top = 'auto';
    } else {
      this.style.transform = 'translateY(-100%)';
      this.style.top = '0';
      this.style.bottom = 'auto';
    }

    this.setupEventListeners();
    this.updateHeaderHeight();
    this.updatePosition();
    this.initializeWithCurrentVariant();
    this.checkVisibility();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.findElements();
    this.setupIntersectionObserver();
    this.setupResizeObserver();
  }

  findElements() {
    this.buyButtons = document.querySelector('.product__buy-buttons--wrapper');
    this.mainProductForm = document.querySelector('product-form');
    this.submitButton = this.querySelector('.sticky-bar__button');
    this.priceElements = {
      compareAtPrice: this.querySelector('.price--compare-at-price'),
      currentPrice: this.querySelector('.price--current-price')
    };
  }

  setupEventListeners() {
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      this.handleVariantChange.bind(this)
    );

    this.scrollHandler = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });

    this.resizeHandler = this.handleResize.bind(this);
    window.addEventListener('resize', this.resizeHandler);
  }

  setupIntersectionObserver() {
    if (!this.buyButtons || !window.IntersectionObserver) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const visible = entry.isIntersecting && entry.intersectionRatio > 0.01;
          this.handleBuyButtonsVisibility(visible);
        });
      },
      { root: null, rootMargin: '0px', threshold: [0, 0.01, 0.1] }
    );

    this.intersectionObserver.observe(this.buyButtons);
  }

  setupResizeObserver() {
    if (!window.ResizeObserver) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.updateHeaderHeight();
      this.checkVisibility();
    });

    const header = document.querySelector('sticky-header') || document.querySelector('header');
    if (header) this.resizeObserver.observe(header);
  }

  handleBuyButtonsVisibility(isVisible) {
    if (isVisible) {
      this.resetToHiddenState();
      return;
    }
    this.showStickyBar();
  }

  resetToHiddenState() {
    this.isVisible = false;
    this.classList.remove('show');
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      this.style.bottom = '0';
      this.style.top = 'auto';
      this.style.transform = 'translateY(100%)';
    } else {
      this.style.top = '0';
      this.style.bottom = 'auto';
      this.style.transform = 'translateY(-100%)';
    }
  }

  showStickyBar() {
    if (this.isVisible) return;
    if (this.shouldHideForEdge()) return;
    this.isVisible = true;
    this.updateHeaderHeight();
    this.updatePosition();
    requestAnimationFrame(() => {
      this.classList.add('show');
      this.style.transform = 'translateY(0)';
    });
  }

  hideStickyBar() {
    this.resetToHiddenState();
  }

  updatePosition() {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      this.style.top = 'auto';
      this.style.bottom = '0';
    } else {
      this.style.top = `${this.headerHeight}px`;
      this.style.bottom = 'auto';
    }

    const isHidden = !this.isVisible;
    if (isHidden) {
      this.style.transform = isMobile ? 'translateY(100%)' : 'translateY(-100%)';
    } else {
      this.style.transform = 'translateY(0)';
    }
  }

  updateHeaderHeight() {
    const header = document.querySelector('sticky-header') || document.querySelector('header');
    if (header) {
      const r = header.getBoundingClientRect();
      this.headerHeight = Math.max(0, r.bottom);
    } else {
      this.headerHeight = 0;
    }
  }

  handleScroll() {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) {
      this.updateHeaderHeight();
      this.updatePosition();
    } else {
      this.updatePosition();
    }

    const shouldHideEdge = this.shouldHideForEdge();
    if (shouldHideEdge && this.isVisible) {
      this.hideStickyBar();
      return;
    }

    if (!shouldHideEdge) {
      this.checkVisibility();
    }
  }

  handleResize() {
    this.updateHeaderHeight();
    this.updatePosition();
    this.checkVisibility();
  }

  initializeWithCurrentVariant() {
    if (!this.mainProductForm) return;
    const variantIdInput = this.mainProductForm.querySelector('[name="id"]');
    if (!variantIdInput || !variantIdInput.value) return;
    const productData = this.getProductData();
    if (!productData || !productData.variants) return;
    const currentVariant = productData.variants.find(v => v.id.toString() === variantIdInput.value);
    if (currentVariant) {
      this.updatePrice(currentVariant);
      this.updateButtonState(currentVariant);
    }
  }

  getProductData() {
    const productScript = document.querySelector(`script[type="application/json"][id*="ProductJSON"]`);
    if (productScript) {
      try { return JSON.parse(productScript.textContent); } catch (e) { return null; }
    }
    return null;
  }

  handleVariantChange(event) {
    if (event?.data?.variant) {
      const variant = event.data.variant;
      this.updatePrice(variant);
      this.updateButtonState(variant);
    }
  }

  updatePrice(variant) {
    if (!this.priceElements || !variant) return;
    const { compareAtPrice, currentPrice } = this.priceElements;
    if (currentPrice) currentPrice.textContent = this.formatMoney(variant.price);
    if (compareAtPrice) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareAtPrice.textContent = this.formatMoney(variant.compare_at_price);
        compareAtPrice.style.display = 'inline';
        compareAtPrice.classList.add('show');
      } else {
        compareAtPrice.style.display = 'none';
        compareAtPrice.classList.remove('show');
      }
    }
  }

  updateButtonState(variant) {
    if (!this.submitButton) return;
    const buttonText = this.submitButton.querySelector('span');
    if (!buttonText) return;
    if (!variant) {
      buttonText.textContent = window.variantStrings?.unavailable || 'Unavailable';
      this.submitButton.disabled = true;
    } else if (!variant.available) {
      buttonText.textContent = window.variantStrings?.soldOut || 'Sold out';
      this.submitButton.disabled = true;
    } else {
      buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
      this.submitButton.disabled = false;
    }
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }

  checkVisibility() {
    if (!this.buyButtons) return;
    const rect = this.buyButtons.getBoundingClientRect();
    const isBuyButtonsVisible = this.isElementVisible(rect);

    const shouldHideEdge = this.shouldHideForEdge();
    if (shouldHideEdge) {
      if (this.isVisible) this.hideStickyBar();
      return;
    }

    this.handleBuyButtonsVisibility(isBuyButtonsVisible);
  }

  isElementVisible(rect) {
    if (!rect) return false;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const headerOffset = vw > 768 ? this.headerHeight : 0;
    const top = rect.top;
    const bottom = rect.bottom;
    const horizontallyVisible = rect.right > 0 && rect.left < vw;
    const verticallyVisible = bottom > headerOffset && top < vh;
    return horizontallyVisible && verticallyVisible;
  }

  isAtTop() {
    return window.scrollY <= this.edgeThreshold;
  }

  isAtBottom() {
    const scrollBottom = window.scrollY + window.innerHeight;
    const docHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
    return scrollBottom >= docHeight - this.edgeThreshold;
  }

  shouldHideForEdge() {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return this.isAtTop();
    return this.isAtBottom();
  }

  cleanup() {
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
    if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
  }
}

customElements.define('product-sticky-bar', ProductStickyBar);


class CstmRecommendationProducts extends HTMLElement {
  observer = undefined;
  swiper = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('cstm-recommendation-products');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.initializeSwiper();
        }
      })
      .catch((e) => {
        console.error('Error loading recommendations:', e);
      });
  }

  initializeSwiper() {
    const swiperElement = this.querySelector('.swiper');
    if (!swiperElement) return;

    if (this.swiper) {
      this.swiper.destroy(true, true);
    }

    this.swiper = new Swiper(swiperElement, {
      slidesPerView: 1.4,
      spaceBetween: 20,
      breakpoints: {
        768: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
        1200: {
          slidesPerView: 4,
          spaceBetween: 20,
        },
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
    });
  }

  disconnectedCallback() {
    this.observer?.unobserve(this);
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
  }
}

customElements.define('cstm-recommendation-products', CstmRecommendationProducts);


function setInfoMargin() {
  const images = document.querySelector('.lb-bundle-classic-images')
  const info = document.querySelector('.lb-bundle-classic-info')
  if (!images || !info) return
  const width = images.offsetWidth
  if (window.innerWidth <= 1100) {
    info.style.marginLeft = '0'
  } else {
    info.style.marginLeft = width + 18 + 'px'
  }
}

// Keep this binding out of the global scope shared with app scripts.
{
  const observer = new MutationObserver(() => setInfoMargin())
  observer.observe(document.body, { childList: true, subtree: true })
}

window.addEventListener('resize', setInfoMargin)
setInfoMargin()




const DEBUG_PREFIX = '[BundleSaver]';

class BundleSavingsCalculator {
  constructor(bundleNode) {
    this.bundleNode = bundleNode;
    this.saveElement = null;
    this.resizeTimer = null;
    this.init();
  }

  parsePrice(priceText) {
    if (!priceText) return NaN;
    return parseFloat(priceText.replace(/[$,\s]/g, ''));
  }

  updateSavingsMessage() {
    const priceContainer = this.bundleNode.querySelector('lb-price.hydrated');
    const desktopTarget = this.bundleNode.querySelector('.lb-button-cont.lb-button-bundle');
    if (!priceContainer || !desktopTarget) return;

    const comparePriceEl = priceContainer.querySelector('.lb-price-compare-price');
    const salePriceEl = priceContainer.querySelector('.lb-price-sale-price');
    if (!comparePriceEl || !salePriceEl) return;

    const originalPriceText = comparePriceEl.textContent.trim();
    const salePriceText = salePriceEl.textContent.trim();
    const currencySymbol = originalPriceText.replace(/[\d.,\s]/g, '');

    const originalPrice = this.parsePrice(originalPriceText);
    const salePrice = this.parsePrice(salePriceText);
    if (isNaN(originalPrice) || isNaN(salePrice) || originalPrice <= salePrice) return;

    const difference = originalPrice - salePrice;
    const savingsText = `You save ${difference.toFixed(2)}${currencySymbol}`;

    if (!this.saveElement) {
        this.saveElement = document.createElement('div');
        this.saveElement.className = 'lb-you-save-message';
    }

    // Avoid re-inserting if content hasn't changed
    if (this.saveElement.textContent === savingsText) return;

    this.saveElement.textContent = savingsText;

    const isMobile = window.innerWidth < 768;
    const target = isMobile ? priceContainer : desktopTarget;

    // Remove from previous parent to avoid duplicate
    if (this.saveElement.parentNode && this.saveElement.parentNode !== target) {
        this.saveElement.parentNode.removeChild(this.saveElement);
    }

    target.appendChild(this.saveElement);
}



  init() {
    const waitPrice = setInterval(() => {
      const priceContainer = this.bundleNode.querySelector('lb-price.hydrated');
      if (priceContainer) {
        clearInterval(waitPrice);
        this.updateSavingsMessage();
      }
    }, 100);

    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.updateSavingsMessage(), 250);
    });

    // Observe changes inside the bundle (price updates, checkbox toggles)
    const observer = new MutationObserver(() => this.updateSavingsMessage());
    observer.observe(this.bundleNode, { childList: true, subtree: true, characterData: true, attributes: true });
  }
}

function initBundleSaver() {
  const bundleSelector = '.lb-bundle-products-cont.lb-classic';
  const bundleNode = document.querySelector(bundleSelector);
  if (bundleNode) {
    new BundleSavingsCalculator(bundleNode);
    return;
  }

  const observer = new MutationObserver((mutations, obs) => {
    const node = document.querySelector(bundleSelector);
    if (node) {
      obs.disconnect();
      new BundleSavingsCalculator(node);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleSaver);
} else {
  initBundleSaver();
}
